import { useEffect, useState } from "react";
import { listActiveDiscountsByRestaurant, listEvents, listRestaurants } from "@/data/restaurants";
import { track } from "@/lib/analytics";
import { loadBonusVenues } from "@/data/bonus";
import { useShowcase } from "@/services/showcase";
import type { Discount, Restaurant, RestaurantEvent, RestaurantId } from "@/types/restaurant";

// Stable empty for the demo (its ticker is built separately).
const NO_EVENTS: RestaurantEvent[] = [];

/**
 * The city feed's data: visible venues, ticker events and active discounts,
 * fetched together per city, with an honest error + retry instead of endless
 * skeletons. Also fires the one `page_view` per city view.
 *
 * Inside the `/showcase-cluj` demo the feed is the bundled snapshot, served
 * synchronously: no fetch, no page_view, never a loading frame.
 */
export function useFeedData(city: string | null) {
  const showcase = useShowcase();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [events, setEvents] = useState<RestaurantEvent[]>([]);
  const [discountsByRestaurant, setDiscountsByRestaurant] = useState<
    Record<RestaurantId, Discount[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Bumped by the retry button — re-runs the feed fetch effect.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!city || showcase) return;
    // Warm the bonus venues (5-minute cache) so the detail's Bonus chip is there on open.
    void loadBonusVenues();
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    Promise.all([
      listRestaurants(city, { visibleOnly: true }),
      listEvents(),
      listActiveDiscountsByRestaurant(),
    ]).then(
      ([rs, es, ds]) => {
        if (cancelled) return;
        // Hidden venues (and, by extension, their offers and ticker events)
        // never reach the feed. `visible` is undefined on pre-migration rows,
        // so `!== false` keeps them shown until the column exists.
        setRestaurants(rs.filter((r) => r.visible !== false));
        setEvents(es);
        setDiscountsByRestaurant(ds);
        setLoading(false);
      },
      () => {
        // Offline / Supabase down: swap the infinite skeletons for an honest
        // error + retry instead of leaving the visitor staring at shimmer.
        if (cancelled) return;
        setLoadError(true);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [city, reloadTick, showcase]);

  // One page_view per city view (not on retry) — the backbone of the traffic +
  // DAU/WAU/MAU + guest-vs-signed-in metrics. Carries anon_id only if consented.
  useEffect(() => {
    if (city && !showcase) track("page_view", { city });
  }, [city, showcase]);

  const reload = () => setReloadTick((n) => n + 1);

  if (showcase) {
    return {
      restaurants: showcase.restaurants,
      events: NO_EVENTS,
      discountsByRestaurant: showcase.discountsByRestaurant,
      loading: false,
      loadError: false,
      reload,
    };
  }

  return { restaurants, events, discountsByRestaurant, loading, loadError, reload };
}
